import { Box, Group, Stack, Text, UnstyledButton } from '@mantine/core';
import { IconHome, IconBook, IconCards, IconUser, IconUsers, IconClipboardList } from '@tabler/icons-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/features/auth/store/authStore';
import { usePlatform } from '@/hooks/usePlatform';

/**
 * Bottom Navigation Component
 *
 * Mobile-style bottom tab bar with role-based navigation
 * - Students: Assignments, Stories, Cards, Profile
 * - Parents: Home, Children, Assign, Profile
 *
 * Features:
 * - iOS blur effect (iOS devices)
 * - Safe area support (iPhone notch/home indicator)
 * - Active state highlighting
 * - Haptic feedback on tap
 */
export function BottomNav() {
  const navigate = useNavigate();
  const location = useLocation();
  const user = useAuthStore((state) => state.user);
  const { isIOS } = usePlatform();

  const isStudent = user?.roles?.includes('student');
  const isParent = user?.roles?.includes('parent');

  // Student navigation tabs
  const studentTabs = [
    { icon: IconClipboardList, label: 'Assignments', path: '/assignments' },
    { icon: IconBook, label: 'Stories', path: '/library' },
    { icon: IconCards, label: 'Cards', path: '/flashcards' },
    { icon: IconUser, label: 'Profile', path: '/profile' },
  ];

  // Parent navigation tabs
  const parentTabs = [
    { icon: IconHome, label: 'Home', path: '/parent' },
    { icon: IconUsers, label: 'Children', path: '/mobile/parent/children' },
    { icon: IconBook, label: 'Assign', path: '/mobile/parent/create-assignment' },
    { icon: IconUser, label: 'Profile', path: '/profile' },
  ];

  // Determine which tabs to show based on role
  // If user has both roles, prefer parent view when on parent routes
  const isOnParentRoute = location.pathname.startsWith('/parent') || location.pathname.startsWith('/mobile/parent');
  const tabs = (isParent && (isOnParentRoute || !isStudent)) ? parentTabs : studentTabs;

  const handleNavClick = (path: string) => {
    navigate(path);

    // Haptic feedback on supported devices
    if (navigator.vibrate) {
      navigator.vibrate(50);
    }
  };

  return (
    <Box
      style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        backgroundColor: isIOS ? 'rgba(255, 255, 255, 0.9)' : 'white',
        borderTop: '1px solid #e9ecef',
        paddingBottom: isIOS ? 'env(safe-area-inset-bottom)' : 0,
        paddingTop: '14px',
        backdropFilter: isIOS ? 'blur(10px)' : 'none',
        WebkitBackdropFilter: isIOS ? 'blur(10px)' : 'none',
        zIndex: 100,
      }}
    >
      <Group grow h="56px" gap={0}>
        {tabs.map((tab) => {
          const Icon = tab.icon;
          // For most tabs, exact match. For assignments, match the path or root for students
          const isActive = location.pathname === tab.path ||
            (tab.path === '/assignments' && location.pathname === '/') ||
            (tab.path === '/parent' && location.pathname.startsWith('/parent'));


          return (
            <UnstyledButton
              key={tab.path}
              onClick={() => handleNavClick(tab.path)}
              style={{
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'transform 0.1s ease',
              }}
              onMouseDown={(e) => {
                // Subtle press effect
                e.currentTarget.style.transform = 'scale(0.95)';
              }}
              onMouseUp={(e) => {
                e.currentTarget.style.transform = 'scale(1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'scale(1)';
              }}
            >
              <Stack gap={2} align="center">
                <Icon
                  size={24}
                  stroke={1.5}
                  color={isActive ? 'var(--mantine-color-teal-6)' : '#868e96'}
                  style={{
                    transition: 'color 0.2s ease',
                  }}
                />
                <Text
                  size="xs"
                  fw={isActive ? 600 : 400}
                  c={isActive ? 'teal.6' : 'dimmed'}
                  style={{
                    transition: 'all 0.2s ease',
                  }}
                >
                  {tab.label}
                </Text>
              </Stack>
            </UnstyledButton>
          );
        })}
      </Group>
    </Box>
  );
}
